export interface TranscriptGroupInput {
  id: string;
  ts: number;
  speaker: string;
  speakerKey?: string | null;
  text: string;
}

export interface TranscriptGroup<T extends TranscriptGroupInput> {
  id: string;
  ts: number;
  speaker: string;
  speakerKey?: string | null;
  colorSeed: string;
  text: string;
  entries: T[];
  inlineParts: Array<{ entry: T; prefix: string }>;
}

export interface TranscriptGroupingOptions<T extends TranscriptGroupInput> {
  isBoundaryBetween?: (previous: T, next: T) => boolean;
}

export interface TranscriptGroupingState<T extends TranscriptGroupInput> {
  groups: Array<TranscriptGroup<T>>;
  canonicalByResolvedSpeaker: Map<string, string>;
}

export function groupAdjacentTranscriptEntries<T extends TranscriptGroupInput>(
  entries: T[],
  options: TranscriptGroupingOptions<T> = {},
): Array<TranscriptGroup<T>> {
  return createTranscriptGroupingState(entries, options).groups;
}

export function createTranscriptGroupingState<T extends TranscriptGroupInput>(
  entries: T[],
  options: TranscriptGroupingOptions<T> = {},
): TranscriptGroupingState<T> {
  const canonicalByResolvedSpeaker = new Map<string, string>();
  const groups: Array<TranscriptGroup<T>> = [];

  for (const entry of entries) {
    appendEntryToGroups(groups, entry, canonicalByResolvedSpeaker, options);
  }

  return { groups, canonicalByResolvedSpeaker };
}

export function appendTranscriptEntriesToGroupingState<T extends TranscriptGroupInput>(
  state: TranscriptGroupingState<T>,
  entries: T[],
  options: TranscriptGroupingOptions<T> = {},
): TranscriptGroupingState<T> {
  if (entries.length === 0) return state;
  const canonicalByResolvedSpeaker = new Map(state.canonicalByResolvedSpeaker);
  const groups = state.groups.slice();

  for (const entry of entries) {
    appendEntryToGroups(groups, entry, canonicalByResolvedSpeaker, options);
  }

  return { groups, canonicalByResolvedSpeaker };
}

function appendEntryToGroups<T extends TranscriptGroupInput>(
  groups: Array<TranscriptGroup<T>>,
  entry: T,
  canonicalByResolvedSpeaker: Map<string, string>,
  options: TranscriptGroupingOptions<T>,
) {
  const identity = speakerIdentity(entry, canonicalByResolvedSpeaker);
  const last = groups.at(-1);
  const previous = last?.entries.at(-1);
  if (last && previous && last.colorSeed === identity && !options.isBoundaryBetween?.(previous, entry)) {
    groups[groups.length - 1] = {
      ...last,
      id: entry.id,
      text: joinTranscriptText(last.text, entry.text),
      entries: [...last.entries, entry],
      inlineParts: [...last.inlineParts, { entry, prefix: joinTranscriptPrefix(last.text, entry.text) }],
    };
    return;
  }

  groups.push({
    id: entry.id,
    ts: entry.ts,
    speaker: entry.speaker,
    speakerKey: entry.speakerKey,
    colorSeed: identity,
    text: entry.text.trim(),
    entries: [entry],
    inlineParts: [{ entry, prefix: "" }],
  });
}

function speakerIdentity(
  entry: TranscriptGroupInput,
  canonicalByResolvedSpeaker: Map<string, string>,
): string {
  const resolved = normalizeSpeakerLabel(entry.speaker);
  const explicitKey = entry.speakerKey?.trim();

  if (explicitKey && isProvisionalSpeakerLabel(entry.speaker)) {
    return explicitKey;
  }

  if (!resolved) return explicitKey || "unknown";
  const existing = canonicalByResolvedSpeaker.get(resolved);
  if (existing) return existing;

  const identity = explicitKey || resolved;
  canonicalByResolvedSpeaker.set(resolved, identity);
  return identity;
}

function normalizeSpeakerLabel(speaker: string): string {
  return speaker.trim().replace(/\s+/g, " ").toLowerCase();
}

function isProvisionalSpeakerLabel(speaker: string): boolean {
  return /^speaker(?:\s+\d+|\s*\?)$/i.test(speaker.trim());
}

function joinTranscriptText(left: string, right: string): string {
  const a = left.trim();
  const b = right.trim();
  if (!a) return b;
  if (!b) return a;
  return `${a}${joinTranscriptPrefix(a, b)}${b}`;
}

function joinTranscriptPrefix(left: string, right: string): string {
  const a = left.trim();
  const b = right.trim();
  if (!a || !b) return "";
  if (isTrailingPunctuationSegment(b)) return "";
  return " ";
}

function isTrailingPunctuationSegment(text: string): boolean {
  return /^[,.;:!?%)\]"']+$/.test(text);
}

export function contiguousEntryRange(
  entryIds: string[],
  anchorEntryId: string,
  targetEntryId: string,
): string[] {
  const anchorIndex = entryIds.indexOf(anchorEntryId);
  const targetIndex = entryIds.indexOf(targetEntryId);
  if (anchorIndex < 0 || targetIndex < 0) return [];
  const start = Math.min(anchorIndex, targetIndex);
  const end = Math.max(anchorIndex, targetIndex);
  return entryIds.slice(start, end + 1);
}
